import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SubpageLayout from "@/components/SubpageLayout";
import WorkImage from "@/components/WorkImage";
import { getCaseStudy, getCaseStudies } from "@/lib/api";

/**
 * Groups gallery frames into an alternating full-width / side-by-side rhythm,
 * the layout editorial studios use so a run of images reads as a composed
 * spread rather than a uniform contact sheet.
 */
function galleryRows(gallery) {
    const rows = [];
    let i = 0;
    let full = true;
    while (i < gallery.length) {
        const take = full ? 1 : 2;
        rows.push(
            gallery.slice(i, i + take).map((g, k) => ({ ...g, index: i + k })),
        );
        i += take;
        full = !full;
    }
    return rows;
}

export default function CaseStudyPage() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [siblings, setSiblings] = useState({ prev: null, next: null });

    useEffect(() => {
        setLoading(true);
        setError("");
        // Fetch this case study + all published (for prev/next) in parallel
        Promise.all([
            getCaseStudy(id).catch((e) => {
                throw e;
            }),
            getCaseStudies().catch(() => []),
        ])
            .then(([d, all]) => {
                setItem(d);
                const idx = all.findIndex((x) => x.id === d.id);
                const prev = idx > 0 ? all[idx - 1] : null;
                const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;
                setSiblings({ prev, next });
            })
            .catch((e) => {
                setError(
                    e?.response?.status === 404
                        ? "This case study isn't published."
                        : "Something went wrong.",
                );
            })
            .finally(() => setLoading(false));
    }, [id]);

    return (
        <SubpageLayout back={{ to: "/#work", label: "All work" }}>
            {loading ? (
                <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-32">
                    <p className="eyebrow text-[#1B1B1B]/60">Loading…</p>
                </div>
            ) : error || !item ? (
                <div className="mx-auto max-w-[900px] px-6 md:px-12 lg:px-20 py-32">
                    <p className="eyebrow text-[#1B1B1B]/60 mb-4">§ 404</p>
                    <h1 className="font-serif-editorial text-5xl md:text-7xl leading-[1.02] tracking-[-0.02em] mb-6">
                        Not on the shelf.
                    </h1>
                    <p className="font-sans-tight text-lg text-[#1B1B1B]/75 max-w-md leading-[1.6] font-light">
                        {error || "That case study cannot be found."}
                    </p>
                    <Link
                        to="/#work"
                        className="mt-10 inline-block eyebrow link-underline"
                    >
                        ← Back to work
                    </Link>
                </div>
            ) : (
                <>
                    {/* Masthead */}
                    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-16 md:pt-24">
                        <div className="border-b border-[#E6E2D6] pb-4 flex items-center justify-between text-[10px] md:text-[11px] tracking-[0.24em] uppercase font-sans-tight text-[#1B1B1B]/70">
                            <span>Case Study</span>
                            <span>
                                {item.category}
                                {item.year ? ` · ${item.year}` : ""}
                            </span>
                        </div>
                    </div>

                    {/* Title block */}
                    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-14 md:pt-24 pb-16 md:pb-24">
                        <div className="grid grid-cols-12 gap-6 md:gap-10">
                            <div className="col-span-12 lg:col-span-8">
                                <motion.h1
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                                    className="font-serif-editorial text-[13vw] md:text-[9vw] lg:text-[7vw] leading-[0.95] tracking-[-0.025em]"
                                >
                                    {item.title}
                                </motion.h1>
                                {item.client && (
                                    <p className="mt-6 eyebrow text-[#1B1B1B]/60">
                                        Client — {item.client}
                                    </p>
                                )}
                            </div>
                            {item.description && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1, delay: 0.15 }}
                                    className="col-span-12 lg:col-span-4 lg:pt-8"
                                >
                                    <p className="font-sans-tight text-base md:text-lg leading-[1.65] text-[#1B1B1B]/85 max-w-md font-light">
                                        {item.description}
                                    </p>
                                    {item.external_url && (
                                        <a
                                            href={item.external_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-6 inline-flex items-center gap-3 eyebrow link-underline text-[#A8844F]"
                                        >
                                            View live →
                                        </a>
                                    )}
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* No cover image here, deliberately. The cover's job is to
                        identify the project in the work grid, where it is
                        cropped to a 4:5 tile. Repeating it full width as the
                        first thing inside the case study means the visitor's
                        first sight of the project is the one image that has
                        been cropped, and it delays the actual frames. The
                        gallery is the work. */}

                    {/* Gallery */}
                    {item.gallery && item.gallery.length > 0 && (
                        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
                            <p className="eyebrow text-[#1B1B1B]/60 mb-8">
                                § Selected frames — {item.gallery.length}
                            </p>
                            {/* Editorial rhythm: one full-bleed frame, then a
                                pair, repeating. Frames keep their own aspect
                                ratio, and rows align to the top so mixed
                                orientations still read as a grid. */}
                            <div className="space-y-6 md:space-y-10">
                                {galleryRows(item.gallery).map((row, r) => (
                                    <div
                                        key={r}
                                        className={
                                            row.length === 1
                                                ? ""
                                                : "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start"
                                        }
                                    >
                                        {row.map((g) => (
                                            <motion.div
                                                key={g.index}
                                                initial={{ opacity: 0, y: 30 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, margin: "-8%" }}
                                                transition={{
                                                    duration: 1.1,
                                                    ease: [0.16, 1, 0.3, 1],
                                                }}
                                            >
                                                <WorkImage
                                                    src={g.url}
                                                    alt={`${item.title} — frame ${g.index + 1}`}
                                                />
                                            </motion.div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Foot / next */}
                    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-16 border-t border-[#E6E2D6] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                                — End of case study
                            </p>
                            <p className="font-serif-editorial italic text-2xl text-[#1B1B1B]">
                                Interested in something similar?
                            </p>
                        </div>
                        <Link
                            to="/#contact"
                            className="inline-flex items-center gap-3 px-7 py-4 bg-[#1B1B1B] text-[#F6F3EC] font-sans-tight text-[12px] tracking-[0.2em] uppercase hover:bg-[#A8844F] transition-colors duration-500"
                        >
                            Start a conversation
                            <svg
                                className="w-4 h-4"
                                viewBox="0 0 16 16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.2"
                            >
                                <path d="M1 8h14M9 2l6 6-6 6" />
                            </svg>
                        </Link>
                    </div>

                    {/* Prev / Next case study */}
                    {(siblings.prev || siblings.next) && (
                        <nav
                            data-testid="case-study-nav"
                            className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-16 border-t border-[#E6E2D6] grid grid-cols-1 md:grid-cols-2 gap-8"
                        >
                            {siblings.prev ? (
                                <Link
                                    to={`/work/${siblings.prev.slug || siblings.prev.id}`}
                                    data-testid="case-study-prev"
                                    className="group flex flex-col items-start"
                                >
                                    <span className="eyebrow text-[#1B1B1B]/60 mb-3">
                                        ← Previous
                                    </span>
                                    <span className="font-serif-editorial text-3xl md:text-4xl leading-[1.05] text-[#1B1B1B] transition-colors duration-500 group-hover:text-[#A8844F]">
                                        {siblings.prev.title}
                                    </span>
                                    <span className="mt-2 font-sans-tight text-xs text-[#1B1B1B]/60">
                                        {siblings.prev.category}
                                        {siblings.prev.year ? ` · ${siblings.prev.year}` : ""}
                                    </span>
                                </Link>
                            ) : (
                                <div />
                            )}
                            {siblings.next ? (
                                <Link
                                    to={`/work/${siblings.next.slug || siblings.next.id}`}
                                    data-testid="case-study-next"
                                    className="group flex flex-col items-start md:items-end md:text-right"
                                >
                                    <span className="eyebrow text-[#1B1B1B]/60 mb-3">
                                        Next →
                                    </span>
                                    <span className="font-serif-editorial text-3xl md:text-4xl leading-[1.05] text-[#1B1B1B] transition-colors duration-500 group-hover:text-[#A8844F]">
                                        {siblings.next.title}
                                    </span>
                                    <span className="mt-2 font-sans-tight text-xs text-[#1B1B1B]/60">
                                        {siblings.next.category}
                                        {siblings.next.year ? ` · ${siblings.next.year}` : ""}
                                    </span>
                                </Link>
                            ) : (
                                <div />
                            )}
                        </nav>
                    )}
                </>
            )}
        </SubpageLayout>
    );
}
